import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

function PdfPage({ document, number }: { document: PDFDocumentProxy; number: number }) {
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(100, Math.floor(entry.contentRect.width)));
    });
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!width) return;
    let cancelled = false;
    let task: RenderTask | undefined;
    setError(''); setLoading(true);
    const render = async () => {
      try {
        const page = await document.getPage(number);
        if (cancelled || !canvas.current) return;
        const original = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: Math.min(width / original.width, 2048 / original.height) });
        const ratio = Math.min(window.devicePixelRatio || 1, 2);
        // Render offscreen so a resize never clears/hides the displayed page or
        // changes its scrollbar geometry while PDF.js is still drawing.
        const nextCanvas = window.document.createElement('canvas');
        nextCanvas.width = Math.ceil(viewport.width * ratio);
        nextCanvas.height = Math.ceil(viewport.height * ratio);
        task = page.render({ canvas: nextCanvas, viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
        await task.promise;
        if (cancelled || !canvas.current) return;
        const context = canvas.current.getContext('2d');
        if (!context) throw new Error('This browser could not display the PDF page.');
        canvas.current.width = nextCanvas.width;
        canvas.current.height = nextCanvas.height;
        canvas.current.style.width = `${viewport.width}px`;
        canvas.current.style.height = `${viewport.height}px`;
        context.drawImage(nextCanvas, 0, 0);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not display this PDF page.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void render();
    return () => { cancelled = true; task?.cancel(); };
  }, [document, number, width]);

  return <div ref={container} className="relative w-full min-h-12 flex flex-col items-center gap-3" aria-busy={loading}>
    {loading && <p className="absolute top-2 flex items-center gap-2 text-sm bg-background/95 rounded px-3 py-2" role="status"><Loader2 className="w-4 h-4 animate-spin" />Loading page…</p>}
    {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
    <canvas ref={canvas} role="img" aria-label={`Purchase order page ${number}`} className="block bg-white shadow-sm max-w-full" data-testid="canvas-po-preview" />
  </div>;
}

export function PoPreview({ file, open, onOpenChange }: { file: File | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    let task: ReturnType<typeof import('pdfjs-dist').getDocument> | undefined;
    setDocument(null); setPage(1); setError('');
    const load = async () => {
      try {
        const [pdf, buffer] = await Promise.all([import('pdfjs-dist/legacy/build/pdf.mjs'), file.arrayBuffer()]);
        if (cancelled) return;
        pdf.GlobalWorkerOptions.workerSrc = workerUrl;
        task = pdf.getDocument({ data: new Uint8Array(buffer), useSystemFonts: true });
        const loaded = await task.promise;
        if (!cancelled) setDocument(loaded);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not open this PDF.');
      }
    };
    void load();
    return () => { cancelled = true; void task?.destroy().catch(() => {}); };
  }, [file, open]);

  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="w-[calc(100%_-_1rem)] max-w-5xl h-[90dvh] p-4 sm:p-6 grid-rows-[auto_auto_minmax(0,1fr)]" data-testid="dialog-po-preview">
      <DialogHeader className="pr-6 min-w-0">
        <DialogTitle>Preview PO</DialogTitle>
        <DialogDescription className="break-all">{file?.name} · Previewed locally; not saved in SoraFlo.</DialogDescription>
      </DialogHeader>
      <div className="flex items-center justify-center gap-3">
        <Button size="sm" variant="outline" disabled={!document || page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous PO page" data-testid="button-po-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-sm tabular-nums" data-testid="text-po-page">{document ? `Page ${page} of ${document.numPages}` : 'Loading PDF…'}</span>
        <Button size="sm" variant="outline" disabled={!document || page >= document.numPages} onClick={() => setPage(p => p + 1)} aria-label="Next PO page" data-testid="button-po-next-page"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="overflow-y-scroll overflow-x-auto [scrollbar-gutter:stable] min-h-0 min-w-0 rounded border border-border bg-muted p-2 sm:p-4" data-testid="scroll-po-preview">
        {error ? <p className="text-sm text-destructive" role="alert">{error}</p> : document && <PdfPage key={page} document={document} number={page} />}
      </div>
    </DialogContent>
  </Dialog>;
}
